<template>
  <div class="row">
    <EntityForm :entity-id="shiftId" v-model:entity="shift" v-model:editState="isEditingShift"
      form-name="Shift" api-url="/api/shift" redirect-url="/admin/shifts" model-constructor="Shift"
      v-bind:show-title=true :lock-editing="isLocked">

      <div v-if="shift.state">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="startsAt" class="form-label">Starts <span class="text-danger">*</span></label>
              <input type="datetime-local" class="form-control" id="startsAt" v-model="startsAtLocal"
                :disabled="!isEditingShift" :readonly="!isEditingShift" required>
            </div>

            <div class="mb-3">
              <label for="endsAt" class="form-label">Ends <span class="text-danger">*</span></label>
              <input type="datetime-local" class="form-control" id="endsAt" v-model="endsAtLocal"
                :disabled="!isEditingShift" :readonly="!isEditingShift" required>
              <div id="endsAt-error" class="invalid-feedback d-none">End time must be after start time.</div>
            </div>

            <div class="mb-3">
              <label for="durationHours" class="form-label">Duration (hours)</label>
              <input type="number" class="form-control" id="durationHours" v-model.number="shift.state.durationHours"
                min="0" step="0.25" :disabled="!isEditingShift" :readonly="!isEditingShift">
              <div id="durationHours-error" class="invalid-feedback d-none">Duration hours must be positive.</div>
            </div>

            <div class="mb-3">
              <label class="form-label">Status</label>
              <div>
                <span v-html="statusBadge"></span>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="guaranteedEarnings" class="form-label">Guaranteed Earnings <span class="text-danger">*</span></label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" id="guaranteedEarnings"
                  v-model.number="shift.state.guaranteedEarnings" min="0" step="0.01"
                  :disabled="!isEditingShift" :readonly="!isEditingShift" required>
              </div>
              <div id="guaranteedEarnings-error" class="invalid-feedback d-none">Guaranteed earnings must be positive.</div>
            </div>

            <div class="mb-3">
              <label for="maxDrivers" class="form-label">Max Drivers <span class="text-danger">*</span></label>
              <input type="number" class="form-control" id="maxDrivers" v-model.number="shift.state.maxDrivers"
                min="1" step="1" :disabled="!isEditingShift" :readonly="!isEditingShift" required>
              <div id="maxDrivers-error" class="invalid-feedback d-none">Max drivers must be positive.</div>
            </div>

            <div class="mb-3">
              <label for="serviceJurisdictionId" class="form-label">Service Jurisdiction</label>
              <select class="form-select" id="serviceJurisdictionId" v-model="shift.state.serviceJurisdictionId"
                :disabled="!isEditingShift">
                <option :value="null">-- All Jurisdictions --</option>
                <option v-for="jurisdiction in jurisdictions" :key="jurisdiction.id" :value="jurisdiction.id">
                  {{ jurisdiction.name }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label for="geoZoneId" class="form-label">Geo Zone</label>
              <select class="form-select" id="geoZoneId" v-model="shift.state.geoZoneId"
                :disabled="!isEditingShift">
                <option :value="null">-- All Zones --</option>
                <option v-for="zone in geoZones" :key="zone.id" :value="zone.id">
                  {{ zone.name }}
                </option>
              </select>
              <small class="form-text text-muted">Optional additional scope; a service jurisdiction takes precedence when both are set.</small>
            </div>
          </div>
        </div>
      </div>
    </EntityForm>

    <div v-if="!isNewShift && shift.state" class="col-12 mt-3">
      <button class="btn btn-success me-2" v-if="shift.state.status === 'DRAFT'" :disabled="isPublishing" @click="publishShift">
        <span v-if="isPublishing" class="spinner-border spinner-border-sm me-2"></span>
        {{ isPublishing ? 'Publishing...' : 'Publish' }}
      </button>
      <button class="btn btn-outline-danger" v-if="['DRAFT', 'PUBLISHED'].includes(shift.state.status)" :disabled="isCancelling" @click="cancelShift">
        <span v-if="isCancelling" class="spinner-border spinner-border-sm me-2"></span>
        {{ isCancelling ? 'Cancelling...' : 'Cancel Shift' }}
      </button>
    </div>

    <div v-if="!isNewShift" class="col-12 mt-4">
      <ShiftSignupsTable :shift-id="shiftId" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import EntityForm from './EntityForm.vue';
import { toDatetimeLocal, fromDatetimeLocal } from '../utility/datetimeLocal';
// Nested inside this already-mounted page, so it needs no main.js/window.Vue entry and no
// data-vue-component mount of its own (CLAUDE.md).
import ShiftSignupsTable from './ShiftSignupsTable.vue';
import { shiftStatus } from '../utility/DatatableRenderer';
import { notificationType, showToast } from '../utility/toastNotificationUtility';
import { newEntityId } from '../globals.js';

const props = defineProps({
  shiftId: {
    type: String,
    default: null
  }
});

const shift = ref({});
const isEditingShift = ref(false);
const isPublishing = ref(false);
const isCancelling = ref(false);
const jurisdictions = ref([]);
const geoZones = ref([]);

const isNewShift = computed(() => props.shiftId === 'add' || props.shiftId === newEntityId);
const isLocked = computed(() => ['IN_PROGRESS', 'CLOSED', 'CANCELLED'].includes(shift.value.state?.status));
const statusBadge = computed(() => shift.value.state ? shiftStatus(shift.value.state.status) : '');

// datetime-local inputs need a plain local YYYY-MM-DDTHH:mm value; the model stores/sends
// startsAt/endsAt as ISO-8601 strings with a UTC offset. Convert at the edges rather than changing
// the model.
// The bracketed-IANA-suffix strip below ("...-04:00[America/Toronto]") is belt-and-braces, not a
// format the server actually emits: Play leaves Jackson's WRITE_DATES_WITH_ZONE_ID disabled, so a
// ZonedDateTime always serializes as a bare offset (measured: "-04:00", or "Z" when the value is
// UTC). Kept because it is free and new Date() would return NaN if that ever changed.
const startsAtLocal = computed({
  get: () => toDatetimeLocal(shift.value.state?.startsAt),
  set: (value) => { shift.value.state.startsAt = fromDatetimeLocal(value); }
});

const endsAtLocal = computed({
  get: () => toDatetimeLocal(shift.value.state?.endsAt),
  set: (value) => { shift.value.state.endsAt = fromDatetimeLocal(value); }
});

const fetchJurisdictionsAndZones = async () => {
  try {
    const [jurisdictionsResponse, zonesResponse] = await Promise.all([
      fetch('/api/service-jurisdictions/active'),
      fetch('/api/service-area-zones')
    ]);
    if (jurisdictionsResponse.ok) {
      jurisdictions.value = await jurisdictionsResponse.json();
    }
    if (zonesResponse.ok) {
      geoZones.value = await zonesResponse.json();
    }
  } catch (error) {
    console.error('Error fetching jurisdictions/zones:', error);
  }
};

const publishShift = async () => {
  isPublishing.value = true;
  try {
    const response = await fetch(`/api/shift/${props.shiftId}/publish`, { method: 'POST' });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || 'Unable to publish shift.', type: notificationType.ERROR, delay: 5000 });
      return;
    }
    const updated = await response.json();
    shift.value.state.status = updated.status;
    showToast({ title: 'Success', body: 'Shift published and eligible drivers notified.', type: notificationType.SUCCESS });
  } catch (error) {
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
    console.error('Failed to publish shift', error);
  } finally {
    isPublishing.value = false;
  }
};

const cancelShift = async () => {
  isCancelling.value = true;
  try {
    const response = await fetch(`/api/shift/${props.shiftId}/cancel`, { method: 'POST' });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || 'Unable to cancel shift.', type: notificationType.ERROR, delay: 5000 });
      return;
    }
    const updated = await response.json();
    shift.value.state.status = updated.status;
    showToast({ title: 'Success', body: 'Shift cancelled.', type: notificationType.SUCCESS });
  } catch (error) {
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
    console.error('Failed to cancel shift', error);
  } finally {
    isCancelling.value = false;
  }
};

onMounted(() => {
  fetchJurisdictionsAndZones();
});
</script>
